import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  getGetApiV5UserJourneyStepsQueryKey,
  useGetApiV5UserJourneySteps,
  usePostApiV5UserJourneySteps,
  type GetApiV5UserJourneySteps200DataItem,
  type PostApiV5UserJourneyStepsBody,
  type PostApiV5UserJourneyStepsBodyStatus,
} from '@liscio/api';

export const ONBOARDING_JOURNEY_KEY = 'signup_onboarding';
export const ONBOARDING_SOURCE = 'onboarding';
export const ONBOARDING_SURFACE = 'onboarding_wizard';

export const ONBOARDING_JOURNEY_STEPS_QUERY_PARAMS = {
  journey_key: ONBOARDING_JOURNEY_KEY,
  surface: ONBOARDING_SURFACE,
};

export const ONBOARDING_STAGE_KEYS = {
  onboardingStarted: 'onboarding_started',
  firmDetailsCompleted: 'firm_details_completed',
  contactSelected: 'contact_selected',
  accountSelected: 'account_selected',
  accountSkipped: 'account_skipped',
  fileUploaded: 'file_uploaded',
  aiIngestionCompleted: 'ai_ingestion_completed',
  aiIngestionNoName: 'ai_ingestion_no_name',
  aiIngestionFailed: 'ai_ingestion_failed',
  onboardingCompleted: 'onboarding_completed',
  firmDetailsSkipped: 'firm_details_skipped',
  createAccountAndContactSkipped: 'create_account_and_contact_skipped',
  fileIntelligenceSkipped: 'file_intelligence_skipped',
} as const;

export type OnboardingStageKey =
  (typeof ONBOARDING_STAGE_KEYS)[keyof typeof ONBOARDING_STAGE_KEYS];

export type OnboardingDerivedStatus =
  | 'pending'
  | 'completed'
  | 'skipped'
  | 'stalled'
  | 'not_applicable';

export type RecordOnboardingJourneyStepParams = {
  stageKey: OnboardingStageKey;
  status: PostApiV5UserJourneyStepsBodyStatus;
  metadata?: Record<string, unknown>;
};

const KNOWN_STAGE_KEYS = new Set<string>(Object.values(ONBOARDING_STAGE_KEYS));

function getStepTimestamp(step: GetApiV5UserJourneySteps200DataItem): number {
  const timestamp = step.created_at ?? step.entered_at ?? step.updated_at;
  return timestamp == null ? 0 : new Date(timestamp).getTime();
}

function toDerivedStatus(status?: string | null): OnboardingDerivedStatus {
  switch (status) {
    case 'completed':
      return 'completed';
    case 'skipped':
    case 'dismissed':
      return 'skipped';
    case 'stalled':
      return 'stalled';
    case 'not_applicable':
      return 'not_applicable';
    default:
      return 'pending';
  }
}

function compactMetadata(metadata?: Record<string, unknown>) {
  if (!metadata) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(metadata).filter(([, value]) => value !== undefined)
  );
}

export function deriveOnboardingStageStatuses(
  journeySteps: GetApiV5UserJourneySteps200DataItem[] = []
): Partial<Record<OnboardingStageKey, OnboardingDerivedStatus>> {
  const latestByStage = new Map<string, GetApiV5UserJourneySteps200DataItem>();

  journeySteps.forEach((step) => {
    if (!step.stage_key || !KNOWN_STAGE_KEYS.has(step.stage_key)) {
      return;
    }

    const existing = latestByStage.get(step.stage_key);
    if (!existing || getStepTimestamp(step) >= getStepTimestamp(existing)) {
      latestByStage.set(step.stage_key, step);
    }
  });

  return Array.from(latestByStage.entries()).reduce<
    Partial<Record<OnboardingStageKey, OnboardingDerivedStatus>>
  >((statuses, [stageKey, step]) => {
    statuses[stageKey as OnboardingStageKey] = toDerivedStatus(step.status);
    return statuses;
  }, {});
}

export function buildOnboardingJourneyStepPayload({
  stageKey,
  status,
  metadata,
}: RecordOnboardingJourneyStepParams): PostApiV5UserJourneyStepsBody {
  const payloadMetadata = compactMetadata(metadata);

  return {
    journey_key: ONBOARDING_JOURNEY_KEY,
    stage_key: stageKey,
    status,
    source: ONBOARDING_SOURCE,
    surface: ONBOARDING_SURFACE,
    ...(payloadMetadata && Object.keys(payloadMetadata).length > 0
      ? { metadata: payloadMetadata }
      : {}),
  };
}

export function useOnboardingJourneySteps() {
  const queryClient = useQueryClient();
  const { data: journeySteps } = useGetApiV5UserJourneySteps(
    ONBOARDING_JOURNEY_STEPS_QUERY_PARAMS
  );
  const { mutateAsync: recordJourneyStep } = usePostApiV5UserJourneySteps();

  const stageStatuses = useMemo(
    () => deriveOnboardingStageStatuses(journeySteps?.data ?? []),
    [journeySteps?.data]
  );

  const recordOnboardingJourneyStep = useCallback(
    async (params: RecordOnboardingJourneyStepParams) => {
      try {
        await recordJourneyStep({
          data: buildOnboardingJourneyStepPayload(params),
        });
        queryClient.invalidateQueries({
          queryKey: getGetApiV5UserJourneyStepsQueryKey(
            ONBOARDING_JOURNEY_STEPS_QUERY_PARAMS
          ),
        });
      } catch (error) {
        console.error('Failed to record onboarding journey step', error);
      }
    },
    [queryClient, recordJourneyStep]
  );

  return {
    journeySteps: journeySteps?.data ?? [],
    stageStatuses,
    recordOnboardingJourneyStep,
  };
}
